import { ApiService } from '../ApiService';
import type {
  FixedAsset,
  CreateFixedAssetPayload,
  UpdateFixedAssetPayload,
  GenerateDepreciationPayload,
  DepreciationResult,
} from '../../../types/fixed-assets';

/**
 * API endpoints pour le module Immobilisations (Fixed Assets)
 * @see POST /fixed-assets — Créer une immobilisation
 * @see GET /fixed-assets — Lister les immobilisations
 * @see GET /fixed-assets/:id — Détail d'une immobilisation
 * @see PUT /fixed-assets/:id — Mettre à jour une immobilisation
 * @see DELETE /fixed-assets/:id — Supprimer (si aucun amortissement)
 * @see POST /fixed-assets/depreciation/generate — Générer les amortissements mensuels
 */
export const fixedAssetsApi = {
  /**
   * Lister toutes les immobilisations
   * @see GET /fixed-assets
   */
  getAll: (params?: { fiscalYearId?: string; status?: string; search?: string }) =>
    ApiService.get<{ success: boolean; data: FixedAsset[] }>('/fixed-assets', params),

  /**
   * Récupérer une immobilisation par ID
   * @see GET /fixed-assets/:id
   */
  getById: (id: string) =>
    ApiService.get<{ success: boolean; data: FixedAsset }>(`/fixed-assets/${id}`),

  /**
   * Créer une nouvelle immobilisation
   * @see POST /fixed-assets
   */
  create: (data: CreateFixedAssetPayload) =>
    ApiService.post<{ success: boolean; data: FixedAsset }>('/fixed-assets', data),

  /**
   * Mettre à jour une immobilisation
   * @see PUT /fixed-assets/:id
   */
  update: (id: string, data: UpdateFixedAssetPayload) =>
    ApiService.put<{ success: boolean; data: FixedAsset }>(`/fixed-assets/${id}`, data),

  /**
   * Supprimer une immobilisation (uniquement si aucun amortissement généré)
   * @see DELETE /fixed-assets/:id
   */
  delete: (id: string) =>
    ApiService.delete<{ success: boolean }>(`/fixed-assets/${id}`),

  /**
   * Générer les écritures d'amortissement mensuel pour toutes les immobilisations actives
   * @see POST /fixed-assets/depreciation/generate
   * 
   * Logique métier :
   * - Linéaire : (acquisitionCost - residualValue) / (usefulLifeYears × 12)
   * - Dégressif OHADA : Coefficient 2× le taux linéaire, appliqué sur la VNC
   * - Chaque immobilisation génère : débit 68x (dotation) / crédit 28x (amortissement cumulé)
   */
  generateDepreciation: (data: GenerateDepreciationPayload) =>
    ApiService.post<{ success: boolean; data: DepreciationResult }>('/fixed-assets/depreciation/generate', data),
};
